'use client';

import { useEffect, useState } from 'react';

export type CountdownParts = {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
};

/** Locale-agnostic milestone — map to translated copy at the UI boundary. */
export type CountdownMilestone =
    | { type: 'ended' }
    | { type: 'finalMinute' }
    | { type: 'finalHour' }
    | { type: 'finalDay' }
    | { type: 'daysRemaining'; days: number };

export type CountdownMilestoneMessages = {
    ended: string;
    lessThanOneMinute: string;
    lessThanOneHour: string;
    lessThanOneDay: string;
    /** Template with `{{count}}` for the singular day boundary. */
    daysRemainingOne: string;
    /** Template with `{{count}}` for the plural day boundary. */
    daysRemainingOther: string;
};

export const DEFAULT_COUNTDOWN_MILESTONE_MESSAGES: CountdownMilestoneMessages = {
    ended: 'Countdown ended',
    lessThanOneMinute: 'Less than one minute remaining',
    lessThanOneHour: 'Less than one hour remaining',
    lessThanOneDay: 'Less than one day remaining',
    daysRemainingOne: '{{count}} day remaining',
    daysRemainingOther: '{{count}} days remaining',
};

export const EXPIRED_COUNTDOWN_PARTS: CountdownParts = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: true,
};

export function getCountdownParts(targetDate: Date, now = Date.now()): CountdownParts {
    const remainingMs = targetDate.getTime() - now;

    if (remainingMs <= 0) {
        return EXPIRED_COUNTDOWN_PARTS;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { days, hours, minutes, seconds, isExpired: false };
}

export function formatCountdownUnit(value: number): string {
    return String(Math.max(0, Math.floor(value))).padStart(2, '0');
}

/**
 * Returns a milestone only for meaningful countdown transitions
 * (day/hour boundaries, final minute, expiry) — never for per-second ticks.
 */
export function getCountdownMilestone(previous: CountdownParts, next: CountdownParts): CountdownMilestone | null {
    if (next.isExpired) {
        return previous.isExpired ? null : { type: 'ended' };
    }

    const enteredFinalMinute =
        next.days === 0 &&
        next.hours === 0 &&
        next.minutes === 0 &&
        (previous.minutes > 0 || previous.hours > 0 || previous.days > 0);

    if (enteredFinalMinute) {
        return { type: 'finalMinute' };
    }

    const enteredFinalHour = next.days === 0 && next.hours === 0 && (previous.days > 0 || previous.hours > 0);

    if (enteredFinalHour) {
        return { type: 'finalHour' };
    }

    if (next.days < previous.days) {
        if (next.days === 0) {
            return { type: 'finalDay' };
        }
        return { type: 'daysRemaining', days: next.days };
    }

    return null;
}

function interpolateCount(template: string, count: number): string {
    return template.replace(/\{\{\s*count\s*\}\}/g, String(count));
}

/**
 * Maps a milestone to a screen-reader announcement using caller-supplied copy
 * (i18n strings or explicit labels).
 */
export function formatCountdownMilestoneAnnouncement(
    milestone: CountdownMilestone,
    messages: CountdownMilestoneMessages = DEFAULT_COUNTDOWN_MILESTONE_MESSAGES
): string {
    switch (milestone.type) {
        case 'ended':
            return messages.ended;
        case 'finalMinute':
            return messages.lessThanOneMinute;
        case 'finalHour':
            return messages.lessThanOneHour;
        case 'finalDay':
            return messages.lessThanOneDay;
        case 'daysRemaining':
            return interpolateCount(
                milestone.days === 1 ? messages.daysRemainingOne : messages.daysRemainingOther,
                milestone.days
            );
    }
}

/**
 * @deprecated Prefer {@link getCountdownMilestone} + {@link formatCountdownMilestoneAnnouncement}
 * so announcements can be localized. Kept for callers that still expect English strings.
 */
export function getCountdownMilestoneAnnouncement(
    previous: CountdownParts,
    next: CountdownParts,
    messages: CountdownMilestoneMessages = DEFAULT_COUNTDOWN_MILESTONE_MESSAGES
): string | null {
    const milestone = getCountdownMilestone(previous, next);
    return milestone ? formatCountdownMilestoneAnnouncement(milestone, messages) : null;
}

/** Defaults keep SSR and client aria-labels identical when callers omit overrides. */
export const DEFAULT_COUNTDOWN_LABEL_LOCALE = 'en-GB';
export const DEFAULT_COUNTDOWN_LABEL_TIME_ZONE = 'UTC';
export const DEFAULT_ENDING_ARIA_LABEL = 'Countdown timer ending {{datetime}}';

/**
 * Formats the static timer name. Locale and time zone must be explicit so SSR
 * and the client produce the same string (and AT hears a predictable end time).
 */
export function formatCountdownEndLabel(
    targetDate: Date,
    locale: string = DEFAULT_COUNTDOWN_LABEL_LOCALE,
    timeZone: string = DEFAULT_COUNTDOWN_LABEL_TIME_ZONE,
    endingAriaLabel: string = DEFAULT_ENDING_ARIA_LABEL
): string {
    const formatted = new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone,
    }).format(targetDate);

    return endingAriaLabel.replace(/\{\{\s*datetime\s*\}\}/g, formatted);
}

export function useCountdown(targetDate: Date): CountdownParts {
    const [parts, setParts] = useState(() => getCountdownParts(targetDate));

    useEffect(() => {
        const initial = getCountdownParts(targetDate);
        setParts(initial);

        // Already expired — no ticking work for mounted banners past the deadline.
        if (initial.isExpired) {
            return;
        }

        const intervalId = window.setInterval(() => {
            const next = getCountdownParts(targetDate);
            setParts(next);

            if (next.isExpired) {
                window.clearInterval(intervalId);
            }
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [targetDate]);

    return parts;
}
